<template>
  <!-- Navbar -->
  <header>
    <nav class="navbar navbar-expand-xl fixed-top bg-light py-1 border-bottom bg-opacity-75">
      <div class="container-fluid mt-0">
        <!-- Logo links -->
        <div class="navbar-brand mt-0">
          <div class="d-inline-block d-md-block">
            <a class="me-1 me-md-4 d-inline-block" href="//climate-impetus.eu" target="_blank">
              <img src="@/assets/img/logos/logo.png" alt="Logo" class="align-top logo_impetus"/>
            </a>
            <div class="d-none d-md-inline-block border-start ps-4 py-1 border-secondary-subtle border-2">
              <a class="me-sm-1 me-md-3" href="//www.eurac.edu" target="_blank">
                <img src="@/assets/img/logos/logo_eurac.png" alt="Logo Eurac"
                     class="align-top logo_eurac mt-1"/>
              </a>
              <a class="me-sm-1 me-md-3" href="//waterjade.com" target="_blank">
                <img src="@/assets/img/logos/logo_waterjade.png" alt="Logo Waterjade"
                     class="align-top logo_waterjade mt-1"/>
              </a>
              <a href="//www.bimsarca.tn.it/" target="_blank">
                <img src="@/assets/img/logos/logo_bim.png" alt="Logo BIM"
                     class="align-top logo_bim mt-1"/>
              </a>
            </div>
          </div>
          <div class="d-none d-md-block fw-semibold fst-italic logo_title text-wrap">
            {{ $t('navbar.title') }}
          </div>
        </div>

        <!-- Toggle button for small screens -->
        <button class="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarNav"
                aria-controls="navbarNav" aria-expanded="false" aria-label="Toggle navigation"
                @click="onNavbarToggleClick">
          <span class="navbar-toggler-icon"></span>
        </button>
        <div class="collapse navbar-collapse justify-content-end" id="navbarNav">
          <!-- Logos when collapsed is shown + small screens -->
          <div class="d-md-none text-center my-3">
            <div class="fw-semibold mb-3 fw-semibold fst-italic logo_title text-wrap text-blue">
              {{ $t('navbar.title') }}
            </div>
            <a class="me-2" href="//www.eurac.edu" target="_blank">
              <img src="@/assets/img/logos/logo_eurac.png" alt="Logo Eurac"
                   class="d-inline-block align-top logo_eurac mt-1"/>
            </a>
            <a class="me-2" href="//waterjade.com" target="_blank">
              <img src="@/assets/img/logos/logo_waterjade.png" alt="Logo Waterjade"
                   class="d-inline-block align-top logo_waterjade mt-1"/>
            </a>
            <a href="//www.bimsarca.tn.it/" target="_blank">
              <img src="@/assets/img/logos/logo_bim.png" alt="Logo BIM" class="d-inline-block align-top logo_bim mt-1"/>
            </a>
          </div>
          <ul class="navbar-nav">
            <!-- Dashboard link -->
            <li class="nav-item">
              <router-link class="nav-link" :class="{ active: propActiveMenu === constants.ROUTING.LANDING }"
                           :to="{name: constants.ROUTING.LANDING}" replace>
                <i class="bi bi-house me-1"></i>{{ $t('navbar.home') }}
              </router-link>
            </li>

            <template v-if="propShowSections === false">
              <li class="nav-item">
                <a class="nav-link" role="button" @click="onLinkClick(constants.ROUTING.VISUALIZATION)">
                  <i class="bi bi-binoculars me-1"></i>{{ $t('navbar.visualization') }}
                </a>
              </li>

              <li class="nav-item">
                <a class="nav-link" role="button" @click="onLinkClick(constants.ROUTING.SIMULATION)">
                  <i class="bi bi-geo me-1"></i>{{ $t('navbar.dss') }}
                </a>
              </li>
            </template>

            <template v-else>
              <li class="nav-item">
                <router-link class="nav-link" :class="{ active: propActiveMenu === constants.ROUTING.VISUALIZATION }"
                             :to="{name: constants.ROUTING.VISUALIZATION}" replace>
                  <i class="bi bi-binoculars me-1"></i>{{ $t('navbar.visualization') }}
                </router-link>
              </li>

              <!-- Simulation Map View link -->
              <li class="nav-item">
                <router-link class="nav-link" :class="{ active: propActiveMenu === constants.ROUTING.SIMULATION }"
                             :to="{name: constants.ROUTING.SIMULATION}"
                             replace>
                  <i class="bi bi-geo me-1"></i>{{ $t('navbar.dss') }}
                </router-link>
              </li>
            </template>

            <li class="nav-item dropdown">
              <a class="nav-link dropdown-toggle" href="#" id="navbarDropdownAbout" role="button"
                 data-bs-toggle="dropdown"
                 aria-expanded="false">
                <i class="bi bi-question-circle me-1"></i>
                <span>{{ $t('navbar.support') }}</span>
              </a>
              <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="navbarDropdownAbout">
                <template v-if="propShowFeedback">
                  <li>
                    <button class="dropdown-item" @click="onFeedbackClick">
                      <i class="bi bi-chat-quote-fill me-1"></i>
                      <span>{{ $t('navbar.feedback') }}</span>
                    </button>
                  </li>
                  <li>
                    <hr class="dropdown-divider"/>
                  </li>
                </template>
                <li>
                  <router-link class="dropdown-item" :to="{name: constants.ROUTING.TERMS}" target="_blank">
                    <i class="bi bi-journal-check me-1"></i>
                    <span>{{ $t('navbar.terms_of_service') }}</span>
                  </router-link>
                </li>
                <li>
                  <router-link class="dropdown-item" :to="{name: constants.ROUTING.PRIVACY}" target="_blank">
                    <i class="bi bi-shield-fill-check me-2"></i>
                    <span>{{ $t('navbar.privacy') }}</span>
                  </router-link>
                </li>
              </ul>
            </li>
            <li class="nav-item dropdown">
              <LangDropdown prop-id="navbarDropdown"
                            :prop-locale="this.$i18n.locale"
                            @on-switch-language="afterSwitchLanguage"/>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  </header>
  <!-- Feedback -->
  <template v-if="propShowFeedback === true">
    <OffCanvas
        prop-id="offCanvasFeedback"
        :prop-title="$t('navbar.feedback')"
        prop-position="end"
        :prop-show="feedback.status"
        @on-canvas-hidden="onFeedbackCanvasHidden">
      <Feedback
          :prop-feedback-from-section="propFeedbackFromSection"
          @on-feedback-success="onFeedbackSuccess"
          @on-feedback-error="onFeedbackError"/>
    </OffCanvas>
    <Toast prop-id="toastFeedbackSuccess"
           prop-type="success"
           :prop-trigger="feedback.toast.success"
           :prop-auto-hide="true">
      <template v-slot:body>{{ $t('Feedback.Sent') }}</template>
    </Toast>
    <Toast prop-id="toastFeedbackError"
           prop-type="danger"
           :prop-trigger="feedback.toast.error"
           :prop-auto-hide="true">
      <template v-slot:body>{{ feedback.message.error }}</template>
    </Toast>
  </template>
</template>

<script>
import constants from '@/libs/Constants';
import Toast from "@/components/Toast.vue";
import {getTooltipById} from "@/libs/Helpers";
import Feedback from "@/components/Feedback.vue";
import OffCanvas from "@/components/OffCanvas.vue";
import LangDropdown from "@/components/LangDropdown.vue";

export default {
  name: 'Navbar',
  components: {Toast, OffCanvas, Feedback, LangDropdown},
  emits: ['onSwitchLanguage', 'onFeedbackClose', 'onLinkClick'],
  props: {
    propActiveMenu: String,
    propShowSections: Boolean || null,
    propFeedbackFromSection: String,
    propShowFeedback: Boolean,
    propOpenFeedback: Boolean,
    propCloseFeedback: {type: Boolean, default: null}
  },
  data() {
    return {
      constants: constants,

      currentLocale: this.$i18n.locale, // Set the initial locale
      currentLanguage: this.$i18n.locale,

      //Feedback
      feedback: {
        status: false,
        toast: {
          success: false,
          error: false
        },
        message: {
          error: ''
        }
      },
    };
  },
  watch: {
    propOpenFeedback(newValue, oldValue) {
      this.feedback.status = newValue;
    },
    propCloseFeedback(newValue, oldValue) {
      this.feedback.status = false;
    }
  },
  methods: {
    afterSwitchLanguage() {
      this.$emit('onSwitchLanguage');
    },
    onNavbarToggleClick() {
      let tooltipButtonAbout = getTooltipById('btnAbout');
      if (tooltipButtonAbout !== null)
        tooltipButtonAbout.hide();
    },
    onFeedbackClick() {
      this.feedback.status = true;
    },
    onFeedbackCanvasHidden() {
      this.feedback.status = false;
      this.$emit('onFeedbackClose');
    },
    onFeedbackSuccess() {
      // Show toast from successful result
      this.feedback.toast.success = !this.feedback.toast.success;
      // Close canvas
      this.feedback.status = false;
      this.$emit('onFeedbackClose');
    },
    onFeedbackError(error) {
      this.feedback.message.error = error;
      this.feedback.toast.error = !this.feedback.toast.error;
    },
    onLinkClick(linkName) {
      this.$emit('onLinkClick', linkName);
    }
  },
};
</script>
